import { Sparkles } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";
import type { Lang } from "@/lib/i18n";

interface Props {
  onPick: (text: string) => void;
}

const PROMPTS: Record<Lang, { label: string; value: string }[]> = {
  ES: [
    { label: "📊 Análisis táctico", value: "Hazme un análisis táctico del partido actual: formaciones, presión y zonas calientes." },
    { label: "⚠️ Riesgos tácticos", value: "¿Cuáles son los principales riesgos tácticos para cada equipo en este momento?" },
    { label: "🟨 Tarjetas amarillas", value: "¿Qué jugadores están en riesgo de tarjeta roja por acumulación o conducta?" },
    { label: "🩹 Lesiones", value: "Reporte de lesiones y fatiga: ¿quién debería ser sustituido?" },
    { label: "🌟 Top performers", value: "¿Quiénes son los jugadores más decisivos hasta ahora y por qué?" },
    { label: "🆚 Comparar formaciones", value: "Compara las formaciones de ambos equipos y sugiere ajustes tácticos." },
    { label: "💸 Oportunidades", value: "¿Qué oportunidades de momentum aparecen ahora?" },
    { label: "🎯 Próximo gol", value: "¿Qué equipo tiene más probabilidad de marcar el próximo gol y por qué?" },
    { label: "⚡ Momentum", value: "Analiza el momentum del partido en los últimos 10 minutos." },
  ],
  EN: [
    { label: "📊 Tactical analysis", value: "Give me a tactical breakdown of the current match: formations, pressure and hot zones." },
    { label: "⚠️ Tactical risks", value: "What are the main tactical risks for each team right now?" },
    { label: "🟨 Yellow cards", value: "Which players are at risk of a red card from accumulated yellows or conduct?" },
    { label: "🩹 Injury report", value: "Injury and fatigue report: who should be subbed off?" },
    { label: "🌟 Top performers", value: "Who are the most decisive performers so far and why?" },
    { label: "🆚 Compare formations", value: "Compare both formations and suggest tactical adjustments." },
    { label: "💸 Opportunities", value: "What momentum opportunities appear right now?" },
    { label: "🎯 Next goal", value: "Which team is more likely to score the next goal and why?" },
    { label: "⚡ Momentum", value: "Analyze the match momentum over the last 10 minutes." },
  ],
  PT: [
    { label: "📊 Análise tática", value: "Faça uma análise tática do jogo atual: formações, pressão e zonas quentes." },
    { label: "⚠️ Riscos táticos", value: "Quais são os principais riscos táticos de cada time agora?" },
    { label: "🟨 Cartões amarelos", value: "Quais jogadores estão em risco de cartão vermelho por acúmulo ou conduta?" },
    { label: "🩹 Lesões", value: "Relatório de lesões e fadiga: quem deveria ser substituído?" },
    { label: "🌟 Top performers", value: "Quem são os jogadores mais decisivos até agora e por quê?" },
    { label: "🆚 Comparar formações", value: "Compare as formações dos dois times e sugira ajustes táticos." },
    { label: "💸 Oportunidades", value: "Que oportunidades de momentum aparecem agora?" },
    { label: "🎯 Próximo gol", value: "Qual time tem mais chance de marcar o próximo gol e por quê?" },
    { label: "⚡ Momentum", value: "Analise o momentum do jogo nos últimos 10 minutos." },
  ],
};

export function TacticalQuickPrompts({ onPick }: Props) {
  const { lang, t } = useLanguage();
  const list = PROMPTS[lang as Lang] ?? PROMPTS.ES;
  return (
    <div className="border-t border-border/40 bg-background/30 px-2 py-2">
      <div className="mb-1 flex items-center gap-1 text-[10px] font-bold tracking-widest text-muted-foreground">
        <Sparkles className="h-3 w-3" /> {t("tactical_quick_prompts")}
      </div>
      <div className="flex flex-wrap gap-1">
        {list.map((p) => (
          <button key={p.label}
            onClick={() => {
              void trackEvent("tactical_quick_prompt_clicked", { label: p.label });
              void trackEvent("quick_prompt_selected", { surface: "match_ai", label: p.label });
              void trackEvent("match_ai_chat_query", { source: "quick_prompt" });
              onPick(p.value);
            }}
            className="rounded-full bg-accent/40 px-2 py-1 text-[10px] font-bold text-foreground hover:bg-accent/60">
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}